import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { updateAuthUserLanguage } from '../store/actions/auth.actions';
import { setLanguage } from '../store/actions/language.actions';
import { selectAuthUser } from '../store/selectors/auth.selectors';
import { ApiService } from './api.service';

export type SupportedLanguage = 'ar' | 'en';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly storageKey = 'lang';

  private readonly authUser = this.store.selectSignal(selectAuthUser);

  constructor(
    private readonly translate: TranslateService,
    private readonly store: Store,
    private readonly apiService: ApiService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  async initialize(): Promise<void> {
    const user = this.authUser();

    /*
     * An authenticated user's backend preference
     * is authoritative.
     *
     * If no language has ever been selected,
     * Arabic is the default.
     */
    if (user) {
      const language: SupportedLanguage = this.isSupportedLanguage(user.preferredLanguage)
        ? user.preferredLanguage
        : 'ar';

      await this.applyLanguage(language);

      return;
    }

    /*
     * Anonymous user:
     * restore the locally saved preference.
     */
    const savedLanguage = localStorage.getItem(this.storageKey);

    const language: SupportedLanguage = this.isSupportedLanguage(savedLanguage)
      ? savedLanguage
      : 'ar';

    await this.applyLanguage(language);
  }

  async changeLanguage(language: SupportedLanguage): Promise<void> {
    await this.applyLanguage(language);

    localStorage.setItem(this.storageKey, language);

    const user = this.authUser();

    if (user) {
      await firstValueFrom(this.apiService.updateUserLanguage(language));

      this.store.dispatch(
        updateAuthUserLanguage({
          language,
        })
      );
    }
  }

  async applyUserLanguage(language: string | null | undefined): Promise<void> {
    const resolvedLanguage: SupportedLanguage = this.isSupportedLanguage(language)
      ? language
      : 'ar';

    localStorage.setItem(this.storageKey, resolvedLanguage);

    await this.applyLanguage(resolvedLanguage);
  }

  private async applyLanguage(language: SupportedLanguage): Promise<void> {
    await firstValueFrom(this.translate.use(language));

    const html = this.document.documentElement;

    html.lang = language;
    html.dir = language === 'ar' ? 'rtl' : 'ltr';

    this.document.body.classList.toggle('rtl', language === 'ar');

    this.document.body.classList.toggle('ltr', language !== 'ar');

    this.store.dispatch(
      setLanguage({
        lang: language,
      })
    );
  }

  private isSupportedLanguage(value: string | null | undefined): value is SupportedLanguage {
    return value === 'ar' || value === 'en';
  }
}
